/**
 * Hand-column label for the sample matrix.
 *
 * Backend `hand_quality_status` is only Completed / In Progress / Not Applicable.
 * The matrix used to map every In Progress to "Not Measured", which hid samples
 * that already had partial hand entries. Match Manual QC's notion of a recorded
 * result: non-Keyence, and either passed or a non-zero value.
 */

type HandResultLike = {
	strategy?: string | null;
	value?: string | number | null;
	passed?: boolean | null;
};

const isHandResult = (item: HandResultLike): boolean =>
	item.strategy !== 'Keyence';

/** Same zero sentinel Manual QC uses in getMissingResults. */
const isRecordedHandResult = (item: HandResultLike): boolean => {
	if (!isHandResult(item)) return false;
	if (item.passed === true) return true;
	return item.value != null && String(item.value) !== '0.000000';
};

export const getHandCheckCounts = (
	results?: HandResultLike[] | null,
): { completed: number; total: number } => {
	const handResults = (results ?? []).filter(isHandResult);
	return {
		completed: handResults.filter(isRecordedHandResult).length,
		total: handResults.length,
	};
};

export const hasRecordedHandMeasurement = (
	results?: HandResultLike[] | null,
): boolean => getHandCheckCounts(results).completed > 0;

export const getHandDisplayStatus = (
	handQualityStatus: string,
	fromSample?: HandResultLike[] | null,
): string => {
	if (handQualityStatus !== 'In Progress') return handQualityStatus;
	const { completed, total } = getHandCheckCounts(fromSample);
	if (completed === 0) return 'Not Measured';
	return `In Progress [${completed}/${total}]`;
};

/** Status key for tag color — strips the [n/m] progress suffix. */
export const getHandTagColorStatus = (displayStatus: string): string =>
	displayStatus.startsWith('In Progress') ? 'In Progress' : displayStatus;
